import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { PublicKey } from '@solana/web3.js'; // Import PublicKey for validation
import '../styles.css';

const Register = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    referralCode: '',
    solanaWallet: '',
  });
  const [isReferralEditable, setIsReferralEditable] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const refCode = queryParams.get('ref');
    if (refCode) {
      setFormData((prev) => ({ ...prev, referralCode: refCode }));
      setIsReferralEditable(false);
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Function to validate Solana wallet address
  const isValidSolanaAddress = (address) => {
    try {
      new PublicKey(address); // Attempt to create a PublicKey object
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { fullName, email, password, confirmPassword, dateOfBirth, referralCode, solanaWallet } = formData;

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate Solana wallet address using @solana/web3.js
    if (!solanaWallet || !isValidSolanaAddress(solanaWallet)) {
      setError('Please provide a valid Solana wallet address');
      setLoading(false);
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;

    try {
      // Optimistic UI update: Show dialog immediately
      setShowDialog(true);

      const res = await axios.post(`${apiUrl}/api/auth/register`, {
        full_name: fullName,
        email,
        password,
        date_of_birth: dateOfBirth,
        ref: referralCode || null,
        wallet_address: solanaWallet,
      });

      console.log('Register response:', res.data);

      // Clear form after success
      setFormData({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: '',
        referralCode: isReferralEditable ? '' : referralCode,
        solanaWallet: '',
      });
    } catch (err) {
      console.error('Register error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      setShowDialog(false); // Revert optimistic update on error
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    navigate('/login');
  };

  return (
    <div className="page-container auth-page">
      <section className="content-container container my-4">
        <form className="register-card p-4 mx-auto card-max-width" onSubmit={handleSubmit}>
          <h2 className="text-center mb-4">Register</h2>

          <div className="mb-3">
            <input
              type="text"
              name="fullName"
              className="form-control"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Full Name"
              required
              disabled={loading}
            />
          </div>
          <div className="mb-3">
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              required
              disabled={loading}
            />
          </div>
          <div className="mb-3">
            <input
              type="password"
              name="password"
              className="form-control"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Password"
              required
              disabled={loading}
            />
          </div>
          <div className="mb-3">
            <input
              type="password"
              name="confirmPassword"
              className="form-control"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm Password"
              required
              disabled={loading}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              className="form-control"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>
          <div className="mb-3">
            <input
              type="text"
              name="solanaWallet"
              className="form-control"
              value={formData.solanaWallet}
              onChange={handleInputChange}
              placeholder="Solana Wallet Address"
              required
              disabled={loading}
            />
          </div>
          <div className="mb-3">
            <input
              type="text"
              name="referralCode"
              className="form-control"
              value={formData.referralCode}
              onChange={handleInputChange}
              placeholder="Referral Code (optional)"
              disabled={!isReferralEditable || loading}
            />
          </div>

          {error && <p className="text-danger text-center">{error}</p>}
          <button type="submit" className="btn btn-primary w-100" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
          <p className="text-center mt-3">
            Already have an account? <Link to="/login" className="text-primary">Login here</Link>
          </p>
        </form>

        {showDialog && (
          <div className="modal-overlay" onClick={handleDialogClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <p className="text-center">
                {loading
                  ? 'Registering your account...'
                  : 'A verification email has been sent. Please verify your email to activate your account.'}
              </p>
              {!loading && (
                <button className="btn btn-primary w-100 mt-3" onClick={handleDialogClose}>
                  OK
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

// Inline styles remain unchanged
const styles = `
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  .modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 90%;
    max-width: 400px;
    text-align: center;
  }
`;

const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

export default Register;
